<template>
  <div class="calcFormsForm">
    <div v-if="loading">
      <v-progress-circular
        :size="20"
        color="primary"
        class="mr-2"
        indeterminate
      ></v-progress-circular>
      Loading...
    </div>
    <div v-else-if="calcItem">
      <v-form ref="calculationsForm" v-model="isFormValid" lazy-validation>
        <div class="mb-6">Last modified: {{ lastModified }}</div>
        <div>
          <v-select
            v-model="calcItem.CalcType"
            label="Type of calculation"
            :items="availableCalcTypes"
            :disabled="availableCalcTypes.length === 1"
            :readonly="!showFormButtons"
            :rules="rules"
            dense
            outlined
          ></v-select>
        </div>
        <div>
          <v-text-field
            v-model="calcItem.Name"
            label="Name"
            maxlength="32"
            hint="Max length is 32"
            :readonly="!showFormButtons"
            dense
            outlined
          ></v-text-field>
        </div>
        <div>
          <v-select
            v-model="calcItem.Tag"
            label="Calculation group"
            :items="availableCalcGroups"
            item-text="ShortName"
            item-value="ShortName"
            :readonly="!showFormButtons"
            dense
            outlined
          ></v-select>
        </div>
        <div>
          <v-text-field
            v-model="calcItem.Description"
            label="Description"
            maxlength="150"
            hint="Max length is 150"
            :readonly="!showFormButtons"
            dense
            outlined
          ></v-text-field>
        </div>
        <div>
          <v-btn
            :disabled="!showFormButtons"
            @click="openCalculationSelection"
            color="primary"
            class="mr-1 mb-2"
            text
          >
            Select item
          </v-btn>
          <v-btn
            :disabled="!showFormButtons"
            color="primary"
            text
            class="mr-1 mb-2"
            @click="openDataItem"
          >
            Add data item
          </v-btn>
          <v-btn
            :disabled="!showFormButtons"
            color="primary"
            text
            class="mb-2"
            @click="openAddFunction"
          >
            Add function
          </v-btn>
          <v-textarea
            v-model="calcItem.Expression"
            label="Calculation"
            hint="Max length is 2048"
            maxlength="2048"
            multiple
            :readonly="!showFormButtons"
            dense
            outlined
          ></v-textarea>
        </div>
        <div
          v-if="
            calcItem.CalcType === 'Service' ||
            calcItem.CalcType === 'Numeric' ||
            calcItem.CalcType === 'Boolean'
          "
          class="rounding"
        >
          <div>
            <v-select
              v-model="calcItem.Rounding"
              label="Rounding"
              :items="roundingList"
              :readonly="!showFormButtons"
              dense
              outlined
            ></v-select>
          </div>
          <div>
            <v-select
              v-if="calcItem.CalcType === 'Service'"
              v-model="calcItem.RoundPeriod"
              label="Rounding period"
              :items="roundingPeriodList"
              :readonly="!showFormButtons"
              dense
              outlined
            ></v-select>
          </div>
          <div>
            <v-text-field
              v-model="calcItem.RoundAmount"
              label="Round amount"
              type="number"
              min="0"
              :readonly="!showFormButtons"
              dense
              outlined
            ></v-text-field>
          </div>
        </div>
      </v-form>
      <div v-if="showFormButtons" class="formFooter">
        <div class="btnContainer">
          <v-btn v-if="id" color="error" outlined @click="onDelete" class="mr-4"> Delete </v-btn>
          <v-spacer></v-spacer>
          <!--<v-btn color="primary" outlined @click="onCopy" class="mr-4"> Copy </v-btn>-->
          <!--<v-btn color="primary" outlined @click="onExport" class="mr-4"> Export </v-btn>-->
          <v-btn color="primary" outlined @click="onTest" class="mr-4"> Test </v-btn>
          <v-btn color="primary" outlined @click="onCancel" class="mr-4"> Cancel </v-btn>
          <v-btn color="primary" depressed @click="onSave"> Save </v-btn>
        </div>
      </div>
    </div>
    <div v-else>
      <v-alert type="info" outlined dense>No info found for this element</v-alert>
    </div>
    <Dialog ref="confirmationDialog" v-model="dialog" />
    <CalculationSelection ref="calculationSelection" :levelId="planId" @select="onSelect" />
    <DataItem ref="dataItem" :levelId="planId" @addDataItem="onAddDataItem" />
    <AddFunction ref="addFunction" :calcType="type" @addFunction="onAddFunction" />
  </div>
</template>

<script>
import Dialog from '@/components/layout/Dialog.vue';
import CalculationSelection from './Popups/CalculationSelection.vue';
import AddFunction from './Popups/AddFunction.vue';
import DataItem from './Popups/DataItem.vue';
import Utils from '@/utils';

export default {
  name: 'CalculationsForm',
  components: {
    Dialog,
    CalculationSelection,
    AddFunction,
    DataItem,
  },

  props: {
    id: { type: Number },
    planId: { type: Number },
    type: { type: String },
  },

  data() {
    return {
      loading: false,
      isFormValid: false,
      dialog: false,
      calcItem: null,
      rules: [(value) => !!value || 'Required.'],
      availableCalcGroups: [],

      showSelectItem: true,

      availableCalculationGroups: [],
      roundingList: ['None', 'Nearest', 'Lower', 'Higher'],
      roundingPeriodList: ['Days', 'Month', 'Years'],
    };
  },

  computed: {
    availableCalcTypes() {
      switch (this.type) {
        case 'Other':
          return ['Boolean', 'Numeric', 'String'];
        default:
          return [this.type];
      }
    },
    lastModified() {
      if (this.calcItem) {
        const date = new Date(this.calcItem.DateChanged);
        if (date.getFullYear() <= 1900) return '';
        return Utils.getDateStr(date);
      }
      return null;
    },
    showFormButtons() {
      const userPermission = this.$store.getters.getUserPermission(this.$route.path);
      if (userPermission && userPermission == 'Full') return true;

      return false;
    },
  },

  watch: {
    id() {
      if (this.id) this.getCalcForm();
      else this.createNewCalc();
    },
  },

  created() {
    if (this.id) this.getCalcForm(this.id);
    else this.createNewCalc();

    this.getAvailableCalcGroups();

    if (this.availableCalcTypes.length === 1) {
      this.calcType = this.availableCalcTypes[0];
    }
  },

  methods: {
    createNewCalc() {
      this.calcItem = {
        CalcId: 0,
        PlanId: this.planId,
        GroupId: 0,
        Name: '',
        Description: '',
        CalcType: this.type,
        Expression: '',
        Rounding: '',
        RoundAmount: 0,
        RoundPeriod: '',
        Tag: '',
        Level: '',
        Username: '',
        DateChanged: '0001-01-01T00:00:00',
      };
    },

    onCopy() {
      const isFormValid = this.$refs.calculationsForm.validate();
      if (isFormValid) this.copyData();
    },

    onCancel() {
      if (this.id) this.getCalcForm(this.id);
      else this.createNewCalc();
    },

    onSave() {
      const isFormValid = this.$refs.calculationsForm.validate();
      if (isFormValid) this.saveData();
    },

    onTest() {
      this.$emit('setTestMode');
    },
    async onExport() {
      try {
       var data = {PlanId:this.planId,GroupId:this.GroupId,CalcType:this.type,Calculation:[this.id]};
       const response = await this.$api({
          method: 'POST',
          url: '/Calculation/ExportCalculations',
          data:data,
          responseType: 'blob',
        });

        if (!response.data) {
          console.log('no response');
          const msg = 'Unable to get download path';
          this.$store.commit('setNotificationMessage', { type: 'error', text: msg });
          throw new Error(msg);
        }

        const url = window.URL.createObjectURL(
          new Blob([response.data], {
            type: 'text/xml',
          })
        );
        const link = document.createElement('a');
        link.href = url;
        console.log('response'+url);
        link.setAttribute('download', "calculations.xml");
        document.body.appendChild(link);
        link.click();
      }
      catch(err) {
        console.log(err);
      }
    },
    openCalculationSelection() {
      const selectDialog = this.$refs.calculationSelection;
      selectDialog.open();
    },

    openAddFunction() {
      const functionDialog = this.$refs.addFunction;
      functionDialog.open();
    },

    openDataItem() {
      const functionDialog = this.$refs.dataItem;
      functionDialog.open();
    },

    onSelect(val) {
      if (!this.calcItem.Expression) {
        this.calcItem.Expression = val;
      } else {
        this.calcItem.Expression = this.calcItem.Expression + val;
      }
    },

    onAddFunction(val) {
      if (!this.calcItem.Expression) {
        this.calcItem.Expression = val;
      } else {
        this.calcItem.Expression = this.calcItem.Expression + val;
      }
    },

    onAddDataItem(val) {
      if (!this.calcItem.Expression) {
        this.calcItem.Expression = val;
      } else {
        this.calcItem.Expression = this.calcItem.Expression + val;
      }
    },

    async onDelete() {
      try {
        await this.$refs.confirmationDialog.open(
          'Confirmation',
          'Are you sure you wish to delete this record?'
        );
        this.deleteCalc();
      } catch (err) {
        console.log();
      }
    },

    async getCalcForm() {
      this.calcItem = null;
      this.loading = true;
      console.log('get calc form'+this.id+' '+this.type);
      try {
        // FIXME: correct method url 'Calulation' to 'Calculation'
        let response = await this.$api.get('/CalculationItem/GetCalulationItem?id=' + this.id);

        if (!response.data) {
          const msg = 'Unable to load calculation item with id ' + this.id;
          this.$store.commit('setNotificationMessage', { type: 'error', text: msg });
          throw new Error(msg);
        }

        var data = response.data;
        this.calcItem = data;
      } catch (err) {
        console.error(err);
      } finally {
        this.loading = false;
      }
    },
      async copyData() {
      try {
        let response = await this.$api.post(
          '/CalculationItem/CopyCalculationItem',
          this.calcItem
        );

        this.$store.commit('setNotificationMessage', {
          type: 'success',
          text: 'Item was copied',
        });
        this.$emit('reloadList', 'update', response.data.Value);
      } catch (err) {
        console.error(err);
      }
    },
    async saveData() {
      this.updateDate();
    },

    async updateDate() {
      try {
        let response = await this.$api.post(
          '/CalculationItem/UpdateCalculationItem',
          this.calcItem
        );

        this.$store.commit('setNotificationMessage', {
          type: 'success',
          text: 'Pension element was saved',
        });
        if (this.calcItem.CalcId === 0) {
          this.$emit('reloadList', 'update', response.data.Value);
        } else {
          this.$emit('reloadList', 'update');
          this.getCalcForm();
        }
      } catch (err) {
        console.error(err);
      }
    },

    async deleteCalc() {
      try {
        await this.$api.delete('/CalculationItem/DeleteCalculationItem?id=' + this.id);

        this.$store.commit('setNotificationMessage', {
          type: 'success',
          text: 'Element is deleted',
        });
        this.$emit('reloadList', 'delete');
      } catch (err) {
        console.error(err);
      }
    },

    async getAvailableCalcGroups() {
      try {
        let response = await this.$api.get(
          `/Picklists/GetPicklist?planId=${this.planId}&listType=Calculations`
        );

        this.availableCalcGroups = response.data;
      } catch (err) {
        console.error(err);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .rounding {
    display:flex;
  }
  .rounding div {
    margin-right:20px;
  }
</style>
